import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { User, Mail, Phone, Save, Camera, Lock, Eye, EyeOff, MapPin, Heart, Briefcase, GraduationCap, Globe, AlertCircle, Stethoscope } from 'lucide-react';

interface AlumnoProfile {
  id: number;
  numeroIdentificacionUnico: string;
  nombre: string;
  apellidoPaterno: string;
  apellidoMaterno?: string;
  email?: string;
  telefono?: string;
  telefonoEmergencia?: string;
  domicilio?: string;
  fechaNacimiento?: string;
  lugarNacimiento?: string;
  nacionalidad?: string;
  escolaridad?: string;
  ocupacion?: string;
  padecimientoMedico?: string;
  tratamientoMedico?: string;
  comoConocio?: string;
  carrera?: string;
  fotoUrl?: string;
}

export default function AlumnoProfile() {
  const [profile, setProfile] = useState<AlumnoProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Photo upload state
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  // Como nos conociste - conditional field
  const [showOtroInput, setShowOtroInput] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    if (profile?.comoConocio && profile.comoConocio.startsWith('Otro:')) {
      setShowOtroInput(true);
    }
  }, [profile?.comoConocio]);

  const fetchProfile = async () => {
    try {
      const response = await fetch('/api/alumnos/me', {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Error al cargar perfil');
      }

      const data = await response.json();
      setProfile(data.alumno);
      if (data.alumno.fotoUrl) {
        setPhotoPreview(data.alumno.fotoUrl);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar perfil');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile) return;

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch('/api/alumnos/me', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          telefono: profile.telefono,
          telefonoEmergencia: profile.telefonoEmergencia,
          domicilio: profile.domicilio,
          fechaNacimiento: profile.fechaNacimiento,
          lugarNacimiento: profile.lugarNacimiento,
          nacionalidad: profile.nacionalidad,
          escolaridad: profile.escolaridad,
          ocupacion: profile.ocupacion,
          padecimientoMedico: profile.padecimientoMedico,
          tratamientoMedico: profile.tratamientoMedico,
          comoConocio: profile.comoConocio,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Error al actualizar perfil');
      }

      setSuccess('Perfil actualizado correctamente');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al actualizar');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (2MB)
    if (file.size > 2 * 1024 * 1024) {
      setError('La imagen no debe superar los 2MB');
      return;
    }

    // Validate file type
    if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
      setError('Solo se permiten imágenes JPG o PNG');
      return;
    }

    setUploadingPhoto(true);
    setError('');
    setSuccess('');

    try {
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);

      // Upload to server
      const formData = new FormData();
      formData.append('foto', file);

      const response = await fetch('/api/alumnos/me/foto', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Error al subir foto');
      }

      const result = await response.json();
      
      // Update profile with new photo URL
      if (profile) {
        setProfile({ ...profile, fotoUrl: result.fotoUrl });
      }

      setSuccess('Foto actualizada correctamente');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al subir foto');
      // Reset preview on error
      if (profile?.fotoUrl) {
        setPhotoPreview(profile.fotoUrl);
      } else {
        setPhotoPreview(null);
      }
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setPasswordError('Las contraseñas no coinciden');
      return;
    }

    // Validate password length
    if (newPassword.length < 8) {
      setPasswordError('La contraseña debe tener al menos 8 caracteres');
      return;
    }

    setChangingPassword(true);

    try {
      const response = await fetch('/api/alumnos/me/password', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          currentPassword,
          newPassword,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Error al cambiar contraseña');
      }

      setPasswordSuccess('Contraseña actualizada correctamente');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setPasswordSuccess(''), 3000);
    } catch (err) {
      setPasswordError(err instanceof Error ? err.message : 'Error al cambiar contraseña');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleComoConocioChange = (value: string) => {
    if (value === 'Otro') {
      setShowOtroInput(true);
      setProfile({ ...profile!, comoConocio: 'Otro: ' });
    } else {
      setShowOtroInput(false);
      setProfile({ ...profile!, comoConocio: value });
    }
  };

  if (loading) {
    return <div className="text-center py-8">Cargando perfil...</div>;
  }

  if (!profile) {
    return (
      <Alert variant="destructive">
        <AlertDescription>No se pudo cargar el perfil</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Mi Perfil</h1>
        <p className="text-gray-600 mt-2">Gestiona tu información personal</p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="bg-green-50 text-green-800 border-green-200">
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Photo Card */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle>Foto de Perfil</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-center">
              <div className="relative inline-block">
                <div className="w-32 h-32 mx-auto rounded-full overflow-hidden bg-blue-100 flex items-center justify-center">
                  {photoPreview ? (
                    <img 
                      src={photoPreview} 
                      alt="Foto de perfil" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User className="w-16 h-16 text-blue-600" />
                  )}
                </div>
                <label 
                  htmlFor="photo-upload" 
                  className="absolute bottom-0 right-0 bg-blue-600 text-white p-2 rounded-full cursor-pointer hover:bg-blue-700 transition-colors"
                >
                  <Camera className="w-4 h-4" />
                  <input
                    id="photo-upload"
                    type="file"
                    accept="image/jpeg,image/jpg,image/png"
                    onChange={handlePhotoChange}
                    className="hidden"
                    disabled={uploadingPhoto}
                  />
                </label>
              </div>
              <h3 className="text-lg font-semibold mt-4">
                {profile.nombre} {profile.apellidoPaterno}
              </h3>
              <p className="text-sm text-gray-600">{profile.numeroIdentificacionUnico}</p>
              {uploadingPhoto && (
                <p className="text-sm text-blue-600 mt-2">Subiendo foto...</p>
              )}
            </div>

            <div className="pt-4 border-t space-y-2">
              <div className="flex items-center text-sm">
                <Mail className="w-4 h-4 mr-2 text-gray-400" />
                <span className="text-gray-600">{profile.email || 'No especificado'}</span>
              </div>
              <div className="flex items-center text-sm">
                <Phone className="w-4 h-4 mr-2 text-gray-400" />
                <span className="text-gray-600">{profile.telefono || 'No especificado'}</span>
              </div>
            </div>

            <div className="pt-4 border-t text-xs text-gray-500">
              <p>Formatos permitidos: JPG, PNG</p>
              <p>Tamaño máximo: 2MB</p>
            </div>
          </CardContent>
        </Card>

        {/* Personal Data Card */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Datos Personales</CardTitle>
            <CardDescription>
              Actualiza tu información de contacto y datos personales
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Información Básica */}
              <div className="space-y-4">
                <h3 className="font-medium text-gray-900 flex items-center">
                  <User className="w-4 h-4 mr-2" />
                  Información Básica
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium">NUI</Label>
                    <Input
                      value={profile.numeroIdentificacionUnico}
                      disabled
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label className="text-sm font-medium">Email</Label>
                    <Input
                      type="email"
                      value={profile.email || ''}
                      disabled
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label className="text-sm font-medium">Nombre</Label>
                    <Input
                      value={profile.nombre}
                      disabled
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label className="text-sm font-medium">Apellido Paterno</Label>
                    <Input
                      value={profile.apellidoPaterno}
                      disabled
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label className="text-sm font-medium">Apellido Materno</Label>
                    <Input
                      value={profile.apellidoMaterno || ''}
                      disabled
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label className="text-sm font-medium">Fecha de Nacimiento</Label>
                    <Input
                      type="date"
                      value={profile.fechaNacimiento || ''}
                      onChange={(e) => setProfile({ ...profile, fechaNacimiento: e.target.value })}
                      className="mt-1"
                      placeholder="YYYY-MM-DD"
                    />
                  </div>

                  <div>
                    <Label className="text-sm font-medium flex items-center">
                      <MapPin className="w-3 h-3 mr-1" />
                      Lugar de Nacimiento
                    </Label>
                    <Input
                      value={profile.lugarNacimiento || ''}
                      onChange={(e) => setProfile({ ...profile, lugarNacimiento: e.target.value })}
                      className="mt-1"
                      placeholder="Ciudad, Estado"
                    />
                  </div>

                  <div>
                    <Label className="text-sm font-medium flex items-center">
                      <Globe className="w-3 h-3 mr-1" />
                      Nacionalidad
                    </Label>
                    <Input
                      value={profile.nacionalidad || ''}
                      onChange={(e) => setProfile({ ...profile, nacionalidad: e.target.value })}
                      className="mt-1"
                      placeholder="Ej: Mexicana"
                    />
                  </div>

                  <div>
                    <Label className="text-sm font-medium">Carrera</Label>
                    <Input
                      value={profile.carrera || ''}
                      disabled
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>

              {/* Información de Contacto */}
              <div className="space-y-4 pt-4 border-t">
                <h3 className="font-medium text-gray-900 flex items-center">
                  <Phone className="w-4 h-4 mr-2" />
                  Información de Contacto
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium">Teléfono</Label>
                    <Input
                      type="tel"
                      value={profile.telefono || ''}
                      onChange={(e) => setProfile({ ...profile, telefono: e.target.value })}
                      className="mt-1"
                      placeholder="Ej: 5512345678"
                    />
                  </div>

                  <div>
                    <Label className="text-sm font-medium text-red-600">Teléfono de Emergencia *</Label>
                    <Input
                      type="tel"
                      value={profile.telefonoEmergencia || ''}
                      onChange={(e) => setProfile({ ...profile, telefonoEmergencia: e.target.value })}
                      className="mt-1"
                      placeholder="Contacto de emergencia"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <Label className="text-sm font-medium">Domicilio</Label>
                    <Input
                      value={profile.domicilio || ''}
                      onChange={(e) => setProfile({ ...profile, domicilio: e.target.value })}
                      className="mt-1"
                      placeholder="Calle, número, colonia, ciudad..."
                    />
                  </div>
                </div>
              </div>

              {/* Información Educativa y Laboral */}
              <div className="space-y-4 pt-4 border-t">
                <h3 className="font-medium text-gray-900 flex items-center">
                  <GraduationCap className="w-4 h-4 mr-2" />
                  Información Educativa y Laboral
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium">Escolaridad</Label>
                    <select
                      value={profile.escolaridad || ''}
                      onChange={(e) => setProfile({ ...profile, escolaridad: e.target.value })}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Selecciona...</option>
                      <option value="Primaria">Primaria</option>
                      <option value="Secundaria">Secundaria</option>
                      <option value="Preparatoria">Preparatoria</option>
                      <option value="Licenciatura">Licenciatura</option>
                      <option value="Maestría">Maestría</option>
                      <option value="Doctorado">Doctorado</option>
                    </select>
                  </div>

                  <div>
                    <Label className="text-sm font-medium flex items-center">
                      <Briefcase className="w-3 h-3 mr-1" />
                      Ocupación
                    </Label>
                    <Input
                      value={profile.ocupacion || ''}
                      onChange={(e) => setProfile({ ...profile, ocupacion: e.target.value })}
                      className="mt-1"
                      placeholder="Ej: Estudiante, Empleado, etc."
                    />
                  </div>
                </div>
              </div>

              {/* Información Médica */}
              <div className="space-y-4 pt-4 border-t">
                <h3 className="font-medium text-gray-900 flex items-center">
                  <Stethoscope className="w-4 h-4 mr-2" />
                  Información Médica
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label className="text-sm font-medium flex items-center">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      ¿Padeces alguna Enfermedad?
                    </Label>
                    <Input
                      value={profile.padecimientoMedico || ''}
                      onChange={(e) => setProfile({ ...profile, padecimientoMedico: e.target.value })}
                      className="mt-1"
                      placeholder="Describe si padeces alguna enfermedad o condición médica"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <Label className="text-sm font-medium flex items-center">
                      <Heart className="w-3 h-3 mr-1" />
                      ¿Estás bajo algún Tratamiento Médico?
                    </Label>
                    <Input
                      value={profile.tratamientoMedico || ''}
                      onChange={(e) => setProfile({ ...profile, tratamientoMedico: e.target.value })}
                      className="mt-1"
                      placeholder="Describe si estás bajo algún tratamiento médico"
                    />
                  </div>
                </div>
              </div>

              {/* Cómo nos Conociste */}
              <div className="space-y-4 pt-4 border-t">
                <h3 className="font-medium text-gray-900">¿Cómo nos Conociste?</h3>
                <div className="space-y-3">
                  <select
                    value={showOtroInput ? 'Otro' : (profile.comoConocio || '')}
                    onChange={(e) => handleComoConocioChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Selecciona una opción...</option>
                    <option value="Instagram">Instagram</option>
                    <option value="Google">Google</option>
                    <option value="Facebook">Facebook</option>
                    <option value="Recomendación">Recomendación</option>
                    <option value="TikTok">TikTok</option>
                    <option value="Camión">Camión</option>
                    <option value="Otro">Otro</option>
                  </select>

                  {showOtroInput && (
                    <div>
                      <Label className="text-sm font-medium">Especifica:</Label>
                      <Input
                        value={profile.comoConocio?.replace('Otro: ', '') || ''}
                        onChange={(e) => setProfile({ ...profile, comoConocio: `Otro: ${e.target.value}` })}
                        className="mt-1"
                        placeholder="Por favor especifica cómo nos conociste"
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button type="submit" disabled={saving}>
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Guardando...' : 'Guardar Cambios'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Password Change Card */}
      <Card>
        <CardHeader>
          <CardTitle>Cambiar Contraseña</CardTitle>
          <CardDescription>
            Actualiza tu contraseña para mantener tu cuenta segura
          </CardDescription>
        </CardHeader>
        <CardContent>
          {passwordError && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{passwordError}</AlertDescription>
            </Alert>
          )}

          {passwordSuccess && (
            <Alert className="bg-green-50 text-green-800 border-green-200 mb-4">
              <AlertDescription>{passwordSuccess}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handlePasswordChange} className="space-y-4 max-w-md">
            <div>
              <Label className="text-sm font-medium">Contraseña Actual</Label>
              <div className="relative mt-1">
                <Input
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Ingresa tu contraseña actual"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium">Nueva Contraseña</Label>
              <div className="relative mt-1">
                <Input
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Mínimo 8 caracteres"
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium">Confirmar Nueva Contraseña</Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repite tu nueva contraseña"
                required
                className="mt-1"
              />
            </div>

            <Button type="submit" disabled={changingPassword}>
              <Lock className="w-4 h-4 mr-2" />
              {changingPassword ? 'Actualizando...' : 'Actualizar Contraseña'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
